'use client'

import Image from 'next/image'
import React from 'react'

import { cn, loadImage } from '@/lib/utils'

import Icon from './icon'

interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  icon?: string
  label: string
  id: string
  value: string | number
  selectedValue: string | number
  image_id?: number
  wrapperClassName?: string
}

const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ icon, label, id, value, selectedValue, image_id, wrapperClassName, className, ...inputProps }, ref) => {
    return (
      <label htmlFor={id} className={cn('cursor-pointer inline-block relative', wrapperClassName)}>
        <input
          ref={ref}
          {...inputProps}
          type="radio"
          id={id}
          value={value}
          checked={value === selectedValue}
          className={cn('opacity-0 absolute top-0', className)}
        />
        <div className="flex items-center space-x-2">
          <div
            className={cn('border border-gray/50 w-5 h-5 rounded-full flex items-center justify-center', {
              'bg-[#337ab7] !border-[#337ab7] text-white': Boolean(value === selectedValue),
              'bg-white': Boolean(value !== selectedValue),
            })}
          >
            <Icon icon={icon ? icon : 'tgic icon-check'} className={value === selectedValue ? 'opacity-100' : 'opacity-0'} />
          </div>
          {image_id && <Image src={loadImage('large', image_id)} alt={image_id.toString()} width={50} height={50} />}
          <span className="hover:text-blue">{label}</span>
        </div>
      </label>
    )
  },
)

Radio.displayName = 'Radio'

export { Radio }
interface RadioCustomProps {
  id?: string
  value?: string | number
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onChange?: (value?: string | number) => void
  label?: string
  checked?: boolean
  disabled?: boolean
  shape?: 'square' | 'round'
  className?: string
}

export default function RadioCustom({ id, value, onClick, onChange, label, checked = true, disabled = false, shape = 'round', className }: RadioCustomProps) {
  const handleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (disabled) return

    onClick?.(event)
    onChange?.(value)
  }

  return (
    <div id={id} onClick={handleClick} className={cn('flex items-center gap-2 cursor-pointer group', { 'cursor-progress': disabled })}>
      <div
        className={cn('w-5 h-5 border border-bordercolor flex items-center justify-center' + (className ? ` ${className}` : ''), {
          'rounded-full': shape === 'round',
          'rounded-sm': shape === 'square',
          'group-hover:border-secondary group-hover:border-2': !checked,
          'bg-secondary text-white border-secondary': checked,
        })}
      >
        {checked && <Icon icon="tgic icon-check" />}
      </div>
      {
        label && <div className="w-[calc(100%-20px)]">{label}</div>
      }
    </div>
  )
}